import {isUndefined} from 'lodash';
import React, {FC, ReactNode} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {SelectAllJobModel} from '../../../../../libs/shared/core/src/models/api/jobModel';
import {Banner, BannerTypesEnum} from '../../../../../libs/shared/ui/src/components/banners/banner';
import {BannerAction} from '../../../../../libs/shared/ui/src/components/banners/bannerAction';
import {BannerCloseAction} from '../../../../../libs/shared/ui/src/components/banners/bannerCloseAction';
import {BannerIcon} from '../../../../../libs/shared/ui/src/components/banners/bannerIcon';
import {BannerProgress} from '../../../../../libs/shared/ui/src/components/banners/bannerProgress';
import {BannerPrioritiesEnum} from '../../../../../libs/shared/ui/src/components/banners/bannersContext';
import {BannerText} from '../../../../../libs/shared/ui/src/components/banners/bannerText';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {BulkActionTypesEnum} from '../../../../helpers/bulkActionHelpers';

/*
 * Props.
 */

interface SelectAllBannerProps {
  job: SelectAllJobModel;
  jobType: BulkActionTypesEnum | undefined;
  onStopClick: () => void;
  onDismissClick: () => void;
}

/*
 * Component.
 */

export const SelectAllBanner: FC<SelectAllBannerProps> = (props) => {
  const theme = useUserTheme();
  const {job, jobType} = props;
  const progress = job.completedStepCount / job.stepCount;

  if (isUndefined(jobType)) {
    return null;
  }

  return (
    <Banner
      bannerType={BannerTypesEnum.INLINE}
      color={progress === 1 ? theme.banner.successBackground : theme.banner.neutralBackground}
      priority={BannerPrioritiesEnum.INFORMATION}
    >
      <BannerProgress progress={progress} />
      {renderBannerIcon(progress)}
      <BannerText>{renderBannerText(jobType, progress)}</BannerText>
      {renderBannerAction(props, progress)}
    </Banner>
  );
};

/*
 * Helpers.
 */

function renderBannerIcon(progress: number) {
  if (progress === 1) {
    return <BannerIcon iconName="checkmarkCircleSmall" />;
  }

  return <BannerIcon iconName="repeatCircleSmall" shouldRotate />;
}

function renderBannerAction(props: SelectAllBannerProps, progress: number) {
  if (progress === 1) {
    return <BannerCloseAction onClick={props.onDismissClick} />;
  }

  return (
    <BannerAction onClick={props.onStopClick}>
      <Trans id="banner-selectall-stop">Stop</Trans>
    </BannerAction>
  );
}

/*
 * Text.
 */

function renderBannerText(jobType: BulkActionTypesEnum, progress: number) {
  if (progress === 1) {
    return bulkActionTypeToCompletionTextMap[jobType];
  }

  return bulkActionTypeToProgressTextMap[jobType];
}

const bulkActionTypeToProgressTextMap: Record<BulkActionTypesEnum, ReactNode> = {
  [BulkActionTypesEnum.ARCHIVE]: (
    <Trans id="banner-selectall-jobrunning-archive">Archiving conversations</Trans>
  ),
  [BulkActionTypesEnum.REOPEN]: (
    <Trans id="banner-selectall-jobrunning-reopen">Reopening conversations</Trans>
  ),
  [BulkActionTypesEnum.SNOOZE]: <Trans id="banner-selectall-jobrunning-snooze">Snoozing conversations</Trans>,
  [BulkActionTypesEnum.UNSNOOZE]: (
    <Trans id="banner-selectall-jobrunning-unsnooze">Unsnoozing conversations</Trans>
  ),
  [BulkActionTypesEnum.TRASH]: <Trans id="banner-selectall-jobrunning-trash">Trashing conversations</Trans>,
  [BulkActionTypesEnum.MOVE]: <Trans id="banner-selectall-jobrunning-move">Moving conversations</Trans>,
  [BulkActionTypesEnum.ASSIGN]: (
    <Trans id="banner-selectall-jobrunning-assign">Assigning conversations</Trans>
  ),
  [BulkActionTypesEnum.UNASSIGN]: (
    <Trans id="banner-selectall-jobrunning-unassign">Unassigning conversations</Trans>
  ),
  [BulkActionTypesEnum.TAG]: <Trans id="banner-selectall-jobrunning-tag">Tagging conversations</Trans>,
  [BulkActionTypesEnum.UNTAG]: <Trans id="banner-selectall-jobrunning-untag">Untagging conversations</Trans>,
  [BulkActionTypesEnum.STAR]: <Trans id="banner-selectall-jobrunning-star">Starring conversations</Trans>,
  [BulkActionTypesEnum.UNSTAR]: (
    <Trans id="banner-selectall-jobrunning-unstar">Unstarring conversations</Trans>
  ),
  [BulkActionTypesEnum.MARK_UNREAD]: (
    <Trans id="banner-selectall-jobfinished-markunread">Marking conversations as unread</Trans>
  ),
  [BulkActionTypesEnum.MARK_READ]: (
    <Trans id="banner-selectall-jobfinished-markread">Marking conversations as read</Trans>
  ),
  [BulkActionTypesEnum.SPAM]: (
    <Trans id="banner-selectall-jobfinished-spam">Marking conversations as spam</Trans>
  ),
};

const bulkActionTypeToCompletionTextMap: Record<BulkActionTypesEnum, ReactNode> = {
  [BulkActionTypesEnum.ARCHIVE]: <Trans id="banner-selectall-jobfinished-archive">Archiving complete</Trans>,
  [BulkActionTypesEnum.REOPEN]: <Trans id="banner-selectall-jobfinished-reopen">Reopening complete</Trans>,
  [BulkActionTypesEnum.SNOOZE]: <Trans id="banner-selectall-jobfinished-snooze">Snoozing complete</Trans>,
  [BulkActionTypesEnum.UNSNOOZE]: (
    <Trans id="banner-selectall-jobfinished-unsnooze">Unsnoozing complete</Trans>
  ),
  [BulkActionTypesEnum.TRASH]: <Trans id="banner-selectall-jobfinished-trash">Trashing complete</Trans>,
  [BulkActionTypesEnum.MOVE]: <Trans id="banner-selectall-jobfinished-move">Moving complete</Trans>,
  [BulkActionTypesEnum.ASSIGN]: <Trans id="banner-selectall-jobfinished-assign">Assigning complete</Trans>,
  [BulkActionTypesEnum.UNASSIGN]: (
    <Trans id="banner-selectall-jobfinished-unassign">Unassigning complete</Trans>
  ),
  [BulkActionTypesEnum.TAG]: <Trans id="banner-selectall-jobfinished-tag">Tagging complete</Trans>,
  [BulkActionTypesEnum.UNTAG]: <Trans id="banner-selectall-jobfinished-untag">Untagging complete</Trans>,
  [BulkActionTypesEnum.STAR]: <Trans id="banner-selectall-jobfinished-star">Starring complete</Trans>,
  [BulkActionTypesEnum.UNSTAR]: <Trans id="banner-selectall-jobfinished-unstar">Unstarring complete</Trans>,
  [BulkActionTypesEnum.MARK_UNREAD]: (
    <Trans id="banner-selectall-jobrunning-markunread">Marking as unread complete</Trans>
  ),
  [BulkActionTypesEnum.MARK_READ]: (
    <Trans id="banner-selectall-jobrunning-markread">Marking as read complete</Trans>
  ),
  [BulkActionTypesEnum.SPAM]: <Trans id="banner-selectall-jobrunning-spam">Marking as spam complete</Trans>,
};
